const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Display labels for license duration / expiration columns. */
export const LicenseDurationLabel = {
    Perpetual: 'Perpetual',
    Expired: 'Expired',
    NotApplicable: 'N/A',
};

export function formatPurchasedDaysLabel(days) {
    return `Days: ${days}`;
}

export function formatRemainingDaysLabel(remaining, purchased) {
    return `${remaining} / ${purchased}`;
}

function toDate(value) {
    if (value == null) {
        return null;
    }
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Epoch milliseconds with sub-ms digits from ISO fractional seconds.
 * `Date` only keeps whole milliseconds; license Created is stamped on SaveChanges
 * slightly after Expiration is computed, so Expiration − Created is often
 * N days minus microseconds. Preserving those digits keeps daysBetween aligned
 * with .NET TimeSpan.Days (which previously drove ExpirationLength).
 */
export function toEpochMs(value) {
    if (value == null) {
        return null;
    }
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null;
    }
    if (value instanceof Date) {
        const ms = value.getTime();
        return Number.isNaN(ms) ? null : ms;
    }

    const str = String(value);
    const date = new Date(str);
    if (Number.isNaN(date.getTime())) {
        return null;
    }

    const fracMatch = str.match(/\.(\d+)/);
    if (!fracMatch) {
        return date.getTime();
    }

    const frac = fracMatch[1];
    if (frac.length <= 3) {
        return date.getTime();
    }

    // Digits beyond milliseconds (e.g. .428793 → +0.793 ms).
    const subMs = Number(`0.${frac.slice(3)}`);
    return date.getTime() + (Number.isFinite(subMs) ? subMs : 0);
}

/** Whole days between two timestamps, matching TimeSpan.Days for positive spans. */
export function daysBetween(start, end) {
    const startMs = toEpochMs(start);
    const endMs = toEpochMs(end);
    if (startMs == null || endMs == null) {
        return null;
    }
    return Math.floor((endMs - startMs) / MS_PER_DAY);
}

/** Purchased duration in days from raw Created → Expiration; null when perpetual. */
export function getLicenseExpirationLengthDays(row) {
    if (row?.licenseExpiration == null) {
        return null;
    }
    return daysBetween(row.licenseCreated, row.licenseExpiration);
}

/**
 * Days remaining for an activated timed key (expirationLength − days since activation).
 * Null when perpetual. For unused keys, returns the purchased duration (not "remaining").
 */
export function getLicenseDaysRemaining(row, now = new Date()) {
    const expirationLength = getLicenseExpirationLengthDays(row);
    if (expirationLength == null) {
        return null;
    }
    if (row?.used !== true || row?.licenseActivation == null) {
        return expirationLength;
    }
    const daysUsed = daysBetween(row.licenseActivation, now);
    if (daysUsed == null) {
        return null;
    }
    return expirationLength - daysUsed;
}

/**
 * Effective end date for an activated key: Activation + (Expiration − Created).
 * Null when perpetual or not activated (portal shows N/A until activation).
 */
export function getEffectiveLicenseExpiration(row) {
    if (row?.licenseExpiration == null) {
        return null;
    }
    if (row?.used !== true || row?.licenseActivation == null) {
        return null;
    }
    const created = toDate(row.licenseCreated);
    const rawExpiration = toDate(row.licenseExpiration);
    const activation = toDate(row.licenseActivation);
    if (!created || !rawExpiration || !activation) {
        return null;
    }
    return new Date(activation.getTime() + (rawExpiration.getTime() - created.getTime()));
}

export function formatLicenseDuration(row, now = new Date()) {
    const expirationLength = getLicenseExpirationLengthDays(row);
    if (expirationLength == null) {
        return LicenseDurationLabel.Perpetual;
    }
    if (row?.used === false) {
        return formatPurchasedDaysLabel(expirationLength);
    }
    const daysRemaining = getLicenseDaysRemaining(row, now);
    if (daysRemaining == null) {
        return LicenseDurationLabel.Perpetual;
    }
    if (daysRemaining <= 0) {
        return LicenseDurationLabel.Expired;
    }
    return formatRemainingDaysLabel(daysRemaining, expirationLength);
}

/** DataGrid valueGetter: remaining/purchased days from raw license dates. */
export function licenseDurationValueGetter(_value, row) {
    return getLicenseDaysRemaining(row);
}

export function formatLicenseExpirationDate(row, options = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
}) {
    if (row?.licenseExpiration == null) {
        return LicenseDurationLabel.Perpetual;
    }
    if (row?.used === false) {
        return LicenseDurationLabel.NotApplicable;
    }
    const effective = getEffectiveLicenseExpiration(row);
    if (!effective) {
        return LicenseDurationLabel.NotApplicable;
    }
    return effective.toLocaleString('en-US', options);
}

/** Sort/display value: unused keys have no activation, even if a leftover date is present. */
export function licenseActivationValueGetter(_value, row) {
    if (row?.used !== true || row?.licenseActivation == null) {
        return null;
    }
    return row.licenseActivation;
}

export function formatLicenseActivationDate(row, options = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
}) {
    const activation = licenseActivationValueGetter(row?.licenseActivation, row);
    const date = toDate(activation);
    if (!date) {
        return LicenseDurationLabel.NotApplicable;
    }
    return date.toLocaleString('en-US', options);
}

/**
 * Compares activation values for DataGrid sorting.
 * Unused / missing activations always sort last, in both directions.
 */
export function compareLicenseActivationValues(left, right, sortDirection = 'asc') {
    const leftMs = toEpochMs(left);
    const rightMs = toEpochMs(right);
    const direction = sortDirection === 'desc' ? -1 : 1;

    if (leftMs == null || rightMs == null) {
        return leftMs == null ? (rightMs == null ? 0 : 1) : -1;
    }
    return (leftMs - rightMs) * direction;
}

/** DataGrid getSortComparator: grid uses the returned fn as-is (does not reverse desc). */
export function getLicenseActivationSortComparator(sortDirection) {
    return (left, right) => compareLicenseActivationValues(left, right, sortDirection);
}
